import { createClient } from '@supabase/supabase-js';

/**
 * Global error reporter — platforms wire this up to their error tracking (Sentry, etc.)
 * Call setErrorReporter(fn) in your app entry point to enable.
 */
let _errorReporter = null;
export function setErrorReporter(fn) { _errorReporter = fn; }
export function reportError(error, context = {}) {
  if (_errorReporter) {
    try { _errorReporter(error, context); } catch (_) {}
  }
}

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

const isConfigured = !!(supabaseUrl && supabaseAnonKey);

if (!isConfigured) {
  console.warn('Missing Supabase environment variables. Check REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY. Supabase client will be null.');
}

export const supabase = isConfigured
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        flowType: 'pkce', // More secure for SPAs
        // Use simple in-process lock instead of navigator.locks to prevent deadlocks
        // caused by React StrictMode double-mount leaving orphaned browser locks.
        // Safe for single-tab usage; multi-tab token refresh is handled by Supabase's
        // BroadcastChannel fallback.
        lock: async (name, acquireTimeout, fn) => {
          return await fn();
        },
      },
    })
  : null;

// Helper to get current session
export const getSession = async () => {
  if (!supabase) return null;
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) {
    console.error('Error getting session:', error);
    return null;
  }
  return session;
};

// Helper to get current user
export const getCurrentUser = async () => {
  if (!supabase) return null;
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) {
    console.error('Error getting user:', error);
    return null;
  }
  return user;
};

// Sign in with Google
export const signInWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      // Redirect to root - AuthContext will handle the code exchange
      redirectTo: window.location.origin,
      queryParams: {
        access_type: 'offline',
        prompt: 'consent',
      },
    },
  });

  if (error) {
    console.error('Google sign in error:', error);
    throw error;
  }

  return data;
};

// Sign in with email/password
export const signInWithEmail = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    console.error('Email sign in error:', error);
    throw error;
  }

  return data;
};

// Sign out
export const signOut = async () => {
  const { error } = await supabase.auth.signOut({ scope: 'global' });
  if (error) {
    console.error('Sign out error:', error);
    throw error;
  }
};

// Check if email is in allowlist (server-side function call)
export const checkEmailAllowed = async (email) => {
  const { data, error } = await supabase
    .rpc('is_email_allowed', { check_email: email });

  if (error) {
    console.error('Error checking email allowlist:', error);
    return { allowed: false, tier: null };
  }

  return data?.[0] || { allowed: false, tier: null };
};

// Get user profile
export const getUserProfile = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle(); // Use maybeSingle instead of single to avoid 406 when no row exists

    if (error) {
      if (error.name === 'AbortError') return null;
      reportError(error, { context: 'getUserProfile', userId });
      return null;
    }

    // If no profile row exists yet, create one with defaults so the backend
    // can always find it (e.g. for usage tracking, tier checks, etc.)
    if (!data) {
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        const email = authUser?.email || '';
        // Check for stored referral code (web: localStorage, set during invite validation)
        let referredBy = null;
        try { if (typeof localStorage !== 'undefined') referredBy = localStorage.getItem('arrivo_referred_by'); } catch {}
        const profileData = { id: userId, email, tier: 'explorer', onboarding_completed: false, updated_at: new Date().toISOString() };
        if (referredBy) profileData.referred_by = referredBy;
        const { data: created, error: createErr } = await supabase
          .from('user_profiles')
          .upsert(profileData, { onConflict: 'id' })
          .select()
          .single();
        // Clear referral code after use
        if (referredBy) { try { localStorage.removeItem('arrivo_referred_by'); } catch {} }
        if (!createErr && created) return created;
      } catch (e) {
        reportError(e, { context: 'getUserProfile.autoCreate', userId });
      }
      return { id: userId, tier: 'explorer', onboarding_completed: false };
    }

    return data;
  } catch (err) {
    if (err?.name === 'AbortError') return null;
    reportError(err, { context: 'getUserProfile', userId });
    return null;
  }
};

// Columns that still exist on user_profiles after the preferences-JSONB migration.
// Anything NOT in this list is treated as a preference and routed into the
// `preferences` JSONB column. This makes legacy callers (e.g. updateUserProfile(
// id, { tripPace: 'relaxed' })) keep working without touching every callsite.
const USER_PROFILE_COLUMNS = new Set([
  'id', 'email', 'display_name', 'avatar_url', 'tier',
  'onboarding_completed', 'quiz_completed', 'welcome_email_sent',
  'analytics_consent', 'analytics_consent_date', 'data_export_requested_at',
  'privacy_policy_accepted_at', 'terms_accepted_at',
  'community_beta_access', 'inner_circle_enabled', 'onboarded_community',
  'profile_visibility', 'community_stats', 'privacy_settings',
  'travel_badge_id', 'top_destinations', 'country', 'taste_profile',
  'suggested_follows_seen', 'bio',
  'stripe_customer_id', 'stripe_subscription_id', 'subscription_status',
  'current_period_end',
  'referral_code', 'referred_by',
  'last_login_at', 'updated_at', 'created_at',
  // New canonical preferences columns (set explicitly when needed):
  'preferences', 'preferences_version', 'preferences_updated_at',
]);

// Update user profile with timeout handling (uses upsert to handle new profiles).
// Splits updates into:
//   - column updates  → direct upsert on user_profiles columns
//   - preference updates → routed into preferences JSONB via merge (canonical)
//
// Legacy field names like `tripPace`, `home_city`, etc. are auto-normalized
// to canonical (`pace`, `homeCity`) by the preferenceSchema normalizer.
export const updateUserProfile = async (userId, updates) => {
  // Lazy import to avoid circular deps in the shared package.
  const { normalizePreferences, preparePreferencesForSave } = await import(
    '../utils/preferenceSchema.js'
  );

  // ── Split updates ──
  const columnUpdates = {};
  const preferenceUpdates = {};
  for (const [key, value] of Object.entries(updates || {})) {
    if (USER_PROFILE_COLUMNS.has(key)) {
      columnUpdates[key] = value;
    } else {
      // Either a canonical preference field or a legacy alias — both go to JSONB
      preferenceUpdates[key] = value;
    }
  }

  const hasColumns = Object.keys(columnUpdates).length > 0;
  const hasPrefs = Object.keys(preferenceUpdates).length > 0;

  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error('Database request timed out after 15 seconds.'));
    }, 15000);
  });

  try {
    // ── Step 1: merge preference updates into existing JSONB ──
    if (hasPrefs) {
      const normalized = normalizePreferences(preferenceUpdates);

      // Read current preferences to merge against
      const { data: current } = await supabase
        .from('user_profiles')
        .select('preferences')
        .eq('id', userId)
        .maybeSingle();

      const merged = { ...(current?.preferences || {}), ...normalized };
      const sanitized = preparePreferencesForSave(merged);

      // The trigger auto-bumps preferences_version + preferences_updated_at
      const { error: prefErr } = await supabase
        .from('user_profiles')
        .upsert(
          {
            id: userId,
            preferences: sanitized,
            updated_at: new Date().toISOString(),
            // For brand-new rows with no email yet, copy the email from columnUpdates if present
            ...(columnUpdates.email ? { email: columnUpdates.email } : {}),
          },
          { onConflict: 'id' },
        );

      if (prefErr) {
        reportError(prefErr, { context: 'updateUserProfile.prefs', userId });
        throw prefErr;
      }
    }

    // ── Step 2: column updates (separate so prefs trigger fires correctly) ──
    let returned = null;
    if (hasColumns) {
      const updatePromise = supabase
        .from('user_profiles')
        .upsert(
          { id: userId, ...columnUpdates, updated_at: new Date().toISOString() },
          { onConflict: 'id' },
        )
        .select()
        .single();

      const { data, error } = await Promise.race([updatePromise, timeoutPromise]);
      if (error) {
        reportError(error, { context: 'updateUserProfile.columns', userId });
        throw error;
      }
      returned = data;
    } else if (hasPrefs) {
      // Re-read so caller gets the merged state including bumped version
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error) {
        reportError(error, { context: 'updateUserProfile.refetch', userId });
        throw error;
      }
      returned = data;
    } else {
      // Nothing to update — re-read for consistency
      const { data } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      returned = data;
    }

    return returned;
  } catch (err) {
    reportError(err, { context: 'updateUserProfile', userId });
    throw err;
  }
};

// Save user feedback preferences (liked/disliked activities, cuisines, etc.)
// This is called when user gives feedback during itinerary chat
// feedbackType: 'activity', 'cuisine', 'neighborhood', 'hotel_style', 'transit', 'pace'
// value: the specific thing being rated (e.g., "Museums", "Italian", "Boutique hotels")
// isPositive: true for thumbs up, false for thumbs down
export const saveUserFeedback = async (userId, feedbackType, value, isPositive) => {
  if (!userId || !value) return null;

  try {
    // Field mapping for different feedback types
    const fieldMap = {
      activity: isPositive ? 'liked_activities' : 'disliked_activities',
      cuisine: isPositive ? 'liked_cuisines' : 'disliked_cuisines',
      neighborhood: 'preferred_neighborhoods',
      hotel_style: 'preferred_hotel_styles',
      transit: 'preferred_transit_modes',
      time_of_day: isPositive ? 'preferred_times' : 'avoided_times',
    };

    const field = fieldMap[feedbackType];
    if (!field) {
      console.warn(`Unknown feedback type: ${feedbackType}`);
      return null;
    }

    // Get current profile
    const { data: profile, error: fetchError } = await supabase
      .from('user_profiles')
      .select(field)
      .eq('id', userId)
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') {
      reportError(fetchError, { context: 'saveUserFeedback.fetch', userId });
      return null;
    }

    // Get current array and add new value (avoid duplicates)
    const currentArray = profile?.[field] || [];
    const normalizedValue = value.toLowerCase().trim();

    if (currentArray.some(v => v.toLowerCase() === normalizedValue)) {
      return profile; // Already saved
    }

    const updatedArray = [...currentArray, value].slice(-100); // Keep last 100

    // Update profile
    const { data, error } = await supabase
      .from('user_profiles')
      .update({ [field]: updatedArray, updated_at: new Date().toISOString() })
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      reportError(error, { context: 'saveUserFeedback.save', userId });
      return null;
    }

    // Preference saved successfully
    return data;
  } catch (err) {
    reportError(err, { context: 'saveUserFeedback', userId });
    return null;
  }
};

// Batch save multiple feedback items (more efficient)
export const saveUserFeedbackBatch = async (userId, feedbackItems) => {
  if (!userId || !feedbackItems?.length) return null;

  try {
    const fieldMap = {
      activity: { positive: 'liked_activities', negative: 'disliked_activities' },
      cuisine: { positive: 'liked_cuisines', negative: 'disliked_cuisines' },
      neighborhood: { positive: 'preferred_neighborhoods', negative: 'preferred_neighborhoods' },
      hotel_style: { positive: 'preferred_hotel_styles', negative: 'preferred_hotel_styles' },
    };

    // Get current profile
    const { data: profile, error: fetchError } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') {
      reportError(fetchError, { context: 'saveUserFeedbackBatch.fetch', userId });
      return null;
    }

    const updates = {};

    for (const item of feedbackItems) {
      const mapping = fieldMap[item.type];
      if (!mapping) continue;

      const field = item.isPositive ? mapping.positive : mapping.negative;
      const currentArray = updates[field] || profile?.[field] || [];
      const normalizedValue = item.value.toLowerCase().trim();

      if (!currentArray.some(v => v.toLowerCase() === normalizedValue)) {
        updates[field] = [...currentArray, item.value].slice(-100);
      }
    }

    if (Object.keys(updates).length === 0) return profile;

    // Update profile
    const { data, error } = await supabase
      .from('user_profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      console.error('Error saving batch feedback:', error);
      return null;
    }

    console.log(`Saved ${feedbackItems.length} user preferences`);
    return data;
  } catch (err) {
    console.error('saveUserFeedbackBatch error:', err);
    return null;
  }
};

// Get monthly usage
export const getMonthlyUsage = async (userId) => {
  // Default usage matches Explorer tier limits
  const defaultUsage = {
    itineraries_generated: 0,
    destination_recommendations: 0,
    ai_modifications: 0,
    itineraries_limit: 1,                    // Explorer: 1/quarter
    destination_recommendations_limit: 5,    // Explorer: 5/quarter
    modifications_limit: 5                   // Explorer: 5/itinerary
  };

  try {
    const { data, error } = await supabase
      .rpc('get_monthly_usage', { p_user_id: userId });

    if (error) {
      // Ignore AbortError
      if (error.name === 'AbortError') return defaultUsage;
      console.error('Error fetching usage:', error);
      return defaultUsage;
    }

    return data?.[0] || defaultUsage;
  } catch (err) {
    // Catch AbortError from fetch
    if (err?.name === 'AbortError') return defaultUsage;
    console.error('getMonthlyUsage error:', err);
    return defaultUsage;
  }
};

// Increment usage
export const incrementUsage = async (userId, type) => {
  const { data, error } = await supabase
    .rpc('increment_usage', { p_user_id: userId, p_type: type });

  if (error) {
    console.error('Error incrementing usage:', error);
    return false;
  }

  return data;
};

// NOTE: checkAndIncrementUsageAtomic removed — was dead code, never called.
// Usage tracking now goes through POST /api/usage/track endpoint.

// Get modification count for a specific itinerary
export const getItineraryModificationCount = async (userId, itineraryId) => {
  const defaultResult = {
    modification_count: 0,
    modification_limit: 5,
    complex_count: 0,
    allows_complex: false
  };

  try {
    const { data, error } = await supabase
      .rpc('get_itinerary_modification_count', {
        p_user_id: userId,
        p_itinerary_id: itineraryId
      });

    if (error) {
      console.error('Error fetching modification count:', error);
      return defaultResult;
    }

    return data?.[0] || defaultResult;
  } catch (err) {
    console.error('getItineraryModificationCount error:', err);
    return defaultResult;
  }
};

// Record a modification for an itinerary
export const recordItineraryModification = async (userId, itineraryId, modificationType, modificationAction = null, isComplex = false) => {
  try {
    const { data, error } = await supabase
      .rpc('record_itinerary_modification', {
        p_user_id: userId,
        p_itinerary_id: itineraryId,
        p_modification_type: modificationType,
        p_modification_action: modificationAction,
        p_is_complex: isComplex
      });

    if (error) {
      console.error('Error recording modification:', error);
      return false;
    }

    return data;
  } catch (err) {
    console.error('recordItineraryModification error:', err);
    return false;
  }
};

// Save itinerary - returns the saved itinerary ID for modification tracking
export const saveItinerary = async (userId, itineraryData, title, destination, startDate_, endDate_, options = {}) => {
  // Normalize empty strings to null — Supabase date columns reject ""
  const startDate = startDate_ || null;
  const endDate = endDate_ || null;
  console.log('Saving itinerary for user:', userId, 'destination:', destination);
  const { expiresAt } = options;

  // First, check if a matching itinerary already exists (same destination and dates)
  // If so, update it instead of creating a duplicate
  // Use .is() for null dates (Supabase can't handle .eq(col, null) — it sends "eq.null" string)
  let query = supabase
    .from('saved_itineraries')
    .select('id')
    .eq('user_id', userId)
    .eq('destination', destination)
    .eq('status', 'saved');
  if (startDate) {
    query = query.eq('start_date', startDate);
  } else {
    query = query.is('start_date', null);
  }
  if (endDate) {
    query = query.eq('end_date', endDate);
  } else {
    query = query.is('end_date', null);
  }
  // When dates are null, also match on title to distinguish different itineraries
  if (!startDate && !endDate) {
    query = query.eq('title', title);
  }
  console.log('💾 [saveItinerary] Querying for existing itinerary...');
  const { data: existingItineraries, error: queryError } = await query.limit(1);
  console.log('💾 [saveItinerary] Query result:', { found: existingItineraries?.length || 0, error: queryError?.message || null });

  if (queryError) {
    console.error('💾 [saveItinerary] Query failed:', queryError);
    throw queryError;
  }

  if (existingItineraries && existingItineraries.length > 0) {
    // Update existing itinerary
    const existingId = existingItineraries[0].id;
    console.log('Updating existing itinerary:', existingId);

    const updateData = {
      title,
      itinerary_data: itineraryData,
      updated_at: new Date().toISOString()
    };
    if (expiresAt) updateData.expires_at = expiresAt;

    const { data, error } = await supabase
      .from('saved_itineraries')
      .update(updateData)
      .eq('id', existingId)
      .select('id')
      .single();

    if (error) {
      console.error('Error updating itinerary:', error.message, error.details, error.hint);
      throw error;
    }

    console.log('Itinerary updated successfully with id:', data?.id);
    return { success: true, id: data?.id, updated: true };
  }

  // No existing match - create new
  const insertData = {
    user_id: userId,
    title,
    destination,
    start_date: startDate || null,
    end_date: endDate || null,
    itinerary_data: itineraryData,
    status: 'saved'
  };
  if (expiresAt) insertData.expires_at = expiresAt;

  const { data, error } = await supabase
    .from('saved_itineraries')
    .insert(insertData)
    .select('id')
    .single();

  if (error) {
    console.error('Error saving itinerary:', error.message, error.details, error.hint);
    throw error;
  }

  console.log('Itinerary saved successfully with id:', data?.id);
  return { success: true, id: data?.id, updated: false };
};

// Get user's saved itineraries
export const getSavedItineraries = async (userId) => {
  const { data, error } = await supabase
    .from('saved_itineraries')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Error fetching itineraries:', error);
    return [];
  }

  // Filter out expired itineraries (Explorer tier 24h auto-saves)
  const now = new Date();
  return (data || []).filter(item => {
    if (!item.expires_at) return true; // No expiry = permanent (Planner/Admin)
    return new Date(item.expires_at) > now;
  });
};

// Delete itinerary
export const deleteItinerary = async (itineraryId, userId) => {
  const { error } = await supabase
    .from('saved_itineraries')
    .delete()
    .eq('id', itineraryId)
    .eq('user_id', userId);

  if (error) {
    console.error('Error deleting itinerary:', error);
    throw error;
  }

  return true;
};

// Update only the user-facing custom title for an itinerary.
// Lets users rename a trip from "Roatán - 7 days" → "Game Family Summer 2026"
// without re-saving the entire itinerary blob. Pass an empty string to clear
// the custom name and revert to the auto-generated display.
export const updateItineraryTitle = async (itineraryId, userId, customTitle) => {
  const trimmed = (customTitle || '').trim();
  const { error } = await supabase
    .from('saved_itineraries')
    .update({
      custom_title: trimmed.length > 0 ? trimmed : null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', itineraryId)
    .eq('user_id', userId);

  if (error) {
    console.error('Error updating itinerary title:', error);
    throw error;
  }

  return { success: true };
};

// ============================================
// ITINERARY FEEDBACK FUNCTIONS
// ============================================

// Save or update feedback for an itinerary item (activity, restaurant, hotel)
export const saveItineraryFeedback = async ({
  userId,
  itineraryId,
  feedbackType, // 'activity', 'restaurant', 'hotel', 'day', 'itinerary'
  dayIndex,
  itemIndex,
  itemName,
  itemLocation,
  itemType, // e.g., 'museum', 'cafe', 'breakfast', 'lunch', 'dinner'
  rating, // 'up' or 'down'
  reason,
  destination
}) => {
  if (!userId || !itemName || !rating) {
    console.warn('saveItineraryFeedback: Missing required fields');
    return null;
  }

  try {
    const { data, error } = await supabase.rpc('upsert_itinerary_feedback', {
      p_user_id: userId,
      p_itinerary_id: itineraryId || null,
      p_feedback_type: feedbackType,
      p_day_index: dayIndex,
      p_item_index: itemIndex,
      p_item_name: itemName,
      p_item_location: itemLocation || null,
      p_item_type: itemType || null,
      p_rating: rating,
      p_reason: reason || null,
      p_destination: destination || null
    });

    if (error) {
      console.error('Error saving itinerary feedback:', error);
      return null;
    }

    console.log(`Feedback saved: ${rating} for "${itemName}" (${feedbackType})`);
    return data?.[0] || { success: true };
  } catch (err) {
    console.error('saveItineraryFeedback error:', err);
    return null;
  }
};

// Delete feedback (for toggling off)
export const deleteItineraryFeedback = async ({
  userId,
  itineraryId,
  dayIndex,
  itemIndex,
  feedbackType
}) => {
  if (!userId) return false;

  try {
    const { data, error } = await supabase.rpc('delete_itinerary_feedback', {
      p_user_id: userId,
      p_itinerary_id: itineraryId || null,
      p_day_index: dayIndex,
      p_item_index: itemIndex,
      p_feedback_type: feedbackType
    });

    if (error) {
      console.error('Error deleting itinerary feedback:', error);
      return false;
    }

    return data;
  } catch (err) {
    console.error('deleteItineraryFeedback error:', err);
    return false;
  }
};

// Get all feedback for an itinerary
export const getItineraryFeedback = async (userId, itineraryId) => {
  if (!userId || !itineraryId) return [];

  try {
    const { data, error } = await supabase.rpc('get_itinerary_feedback', {
      p_user_id: userId,
      p_itinerary_id: itineraryId
    });

    if (error) {
      console.error('Error fetching itinerary feedback:', error);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('getItineraryFeedback error:', err);
    return [];
  }
};

// Update feedback reason (when user provides additional context)
export const updateFeedbackReason = async (userId, itineraryId, dayIndex, itemIndex, feedbackType, reason) => {
  if (!userId) return false;

  try {
    const { error } = await supabase
      .from('itinerary_feedback')
      .update({ reason, updated_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('itinerary_id', itineraryId)
      .eq('day_index', dayIndex)
      .eq('item_index', itemIndex)
      .eq('feedback_type', feedbackType);

    if (error) {
      console.error('Error updating feedback reason:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('updateFeedbackReason error:', err);
    return false;
  }
};

// ============================================
// GDPR FUNCTIONS
// ============================================

// GDPR: Export user data (includes community data)
export const exportUserData = async (userId, reason) => {
  // Safe profile fields — excludes internal/admin fields
  // GDPR Article 15 — minimum legally required: identity, profiling data used for
  // automated decisions, user-generated content, and consent records.
  // Excludes: gamification (badges, achievements), operational flags (onboarding,
  // quiz_completed), referral metadata, community_beta_access, derived/computed data.
  const SAFE_PROFILE_FIELDS = [
    // Identity
    'id', 'display_name', 'avatar_url', 'bio', 'country',
    'home_city', 'passport_country',
    // Preferences used for profiling / automated decisions (itinerary generation)
    'interests', 'dietary_restrictions', 'liked_cuisines', 'disliked_cuisines',
    'accessibility_needs', 'safety_considerations',
    'travel_pace', 'default_budget', 'travel_companions', 'currency',
    'preferred_cabin_class', 'preferred_hotel_tier',
    'favorite_destinations', 'least_favorite_destinations',
    'dream_destinations', 'avoid_places',
    // Consent records (required by GDPR)
    'privacy_settings', 'analytics_consent', 'analytics_consent_date',
    // Timestamps
    'created_at', 'updated_at',
  ].join(', ');

  // Safe itinerary fields — user-created content only
  const SAFE_ITINERARY_FIELDS = [
    'id', 'title', 'destination', 'itinerary_data', 'created_at', 'updated_at',
  ].join(', ');

  // Fetch only legally required data
  const [profileRes, itinerariesRes, reviewsRes] = await Promise.all([
    supabase.from('user_profiles').select(SAFE_PROFILE_FIELDS).eq('id', userId).single(),
    supabase.from('saved_itineraries').select(SAFE_ITINERARY_FIELDS).eq('user_id', userId),
    supabase.from('place_reviews').select('place_name, place_category, user_rating, sentiment, review_text, city, country, created_at').eq('user_id', userId),
  ]);

  // Mark export requested
  await supabase
    .from('user_profiles')
    .update({ data_export_requested_at: new Date().toISOString() })
    .eq('id', userId);

  return {
    export_metadata: {
      exported_at: new Date().toISOString(),
      reason: reason || 'not specified',
      format_version: '1.0',
    },
    profile: profileRes.data,
    itineraries: itinerariesRes.data,
    reviews: reviewsRes.data || [],
  };
};

// GDPR: Delete user account via backend (requires service role key)
// The frontend anon key cannot call admin.deleteUser() — this MUST go through the backend
export const deleteUserAccount = async (userId, accessToken) => {
  const API_BASE = typeof process !== 'undefined' && process.env?.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL
    : (typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_API_URL
      ? process.env.EXPO_PUBLIC_API_URL
      : 'http://localhost:3001');

  const response = await fetch(`${API_BASE}/api/delete-account`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Account deletion failed. Please contact support.');
  }

  return true;
};

// Submit bug report - simple direct insert
export const submitBugReport = async ({
  userId,
  userEmail,
  title,
  description,
  severity = 'medium',
  category = 'general',
  appState = null,
  itinerarySnapshot = null,
  errorLogs = [],
  requestId = null
}) => {
  const bugReportData = {
    user_id: userId || null,
    user_email: userEmail || null,
    title,
    description,
    severity,
    category,
    page_url: typeof window !== 'undefined' ? window.location.href : null,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    screen_size: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : null,
    app_state: appState,
    itinerary_snapshot: itinerarySnapshot,
    error_logs: errorLogs,
    request_id: requestId,
  };

  // Simple insert - no timeout race, just let Supabase handle it
  const { error } = await supabase
    .from('bug_reports')
    .insert(bugReportData);

  if (error) {
    console.error('Bug report insert error:', error);
    throw new Error(`Failed to submit: ${error.message}`);
  }

  return { success: true };
};

// ============================================
// REAL-TIME SYNC: Cross-device (Supabase Realtime) + Cross-tab (BroadcastChannel)
// ============================================

/**
 * Subscribe to saved_itineraries changes for a user.
 * Fires callback on INSERT, UPDATE, DELETE from any device/tab.
 * Returns unsubscribe function.
 */
export const subscribeToItineraryChanges = (userId, onChange) => {
  if (!userId) return () => {};

  const channel = supabase
    .channel(`itineraries:${userId}`)
    .on(
      'postgres_changes',
      {
        event: '*', // INSERT, UPDATE, DELETE
        schema: 'public',
        table: 'saved_itineraries',
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        onChange(payload.eventType, payload.new || payload.old);
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
};

/**
 * Cross-tab sync via BroadcastChannel API.
 * When one tab saves/deletes an itinerary, other tabs get notified.
 */
let itineraryBroadcast = null;

export const broadcastItineraryChange = (eventType, data) => {
  try {
    if (!itineraryBroadcast) {
      itineraryBroadcast = new BroadcastChannel('wander_itineraries');
    }
    itineraryBroadcast.postMessage({ eventType, data, timestamp: Date.now() });
  } catch {
    // BroadcastChannel not supported (e.g., Safari < 15.4) — silent fallback
  }
};

export const onItineraryBroadcast = (callback) => {
  try {
    if (!itineraryBroadcast) {
      itineraryBroadcast = new BroadcastChannel('wander_itineraries');
    }
    itineraryBroadcast.onmessage = (event) => {
      callback(event.data.eventType, event.data.data);
    };
    return () => {
      itineraryBroadcast.onmessage = null;
    };
  } catch {
    return () => {}; // No-op cleanup if unsupported
  }
};

export default supabase;
